import * as React from 'react';
import {
  timelineDotRecipe,
  timelineStepRecipe,
  type TimelineDotRecipeProps,
} from '../recipes/map.recipe';
import { Stack } from './Layout';
import { Text } from './Text';

export interface CallTimelineStep {
  id: string;
  /** "Dispatched", "On scene". */
  label: string;
  /** Already formatted: a time, "ETA 2:41 PM", or "Not yet". */
  time: string;
  state: NonNullable<TimelineDotRecipeProps['state']>;
}

export interface CallTimelineProps {
  steps: CallTimelineStep[];
  /**
   * Makes each step a button, for the history map, where choosing one moves
   * the replay to that moment.
   */
  onSelectStep?: (id: string) => void;
}

/**
 * What has happened on a call and what is still to come, top to bottom.
 *
 *   <CallTimeline steps={[
 *     { id: 'dispatched', label: 'Dispatched', time: '2:21 PM', state: 'done' },
 *     { id: 'arrived', label: 'On scene', time: 'ETA 2:41 PM', state: 'upcoming' },
 *   ]} />
 *
 * A filled dot is done, the primary one is where the call is now, a hollow
 * one has not happened; the upcoming steps are muted as well, so the three
 * are told apart without the colour.
 */
export function CallTimeline({ steps, onSelectStep }: CallTimelineProps) {
  return (
    <Stack gap={0}>
      {steps.map((step) => {
        const content = (
          <>
            <span
              aria-hidden="true"
              className={timelineDotRecipe({ state: step.state })}
            />
            <Text
              as="span"
              variant={step.state === 'current' ? 'body-sm-strong' : 'body-sm'}
              tone={step.state === 'upcoming' ? 'muted' : 'default'}
              className="min-w-0 flex-1"
            >
              {step.label}
            </Text>
            <Text
              as="span"
              variant={step.state === 'current' ? 'body-sm-strong' : 'body-sm'}
              tone={step.state === 'upcoming' ? 'muted' : 'default'}
              numeric
            >
              {step.time}
            </Text>
          </>
        );
        return onSelectStep ? (
          <button
            key={step.id}
            type="button"
            onClick={() => onSelectStep(step.id)}
            className={timelineStepRecipe({ interactive: true })}
          >
            {content}
          </button>
        ) : (
          <div key={step.id} className={timelineStepRecipe()}>
            {content}
          </div>
        );
      })}
    </Stack>
  );
}
